import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCalendar, FiChevronDown, FiChevronRight, FiFilter, FiHash, FiList, FiRefreshCw, FiX } from "react-icons/fi";
import { DATE_PRESETS, activeDatePreset, buildValueOptions, datePresetRange, groupFilterFields, summarizeFilter } from "./panelModel";
import { Highlight, SearchField, SidePanel, ViewsBar } from "./SidePanel";

const TYPE_ICONS = { category: FiList, number: FiHash, date: FiCalendar };
// A partir de este número de valores, la lista de un filtro lleva su propio buscador.
const VALUE_SEARCH_THRESHOLD = 7;
// Grupos de campos que se muestran abiertos al entrar (el resto, plegados).
const QUICK_GROUPS = new Set(["Básicos", "Indicadores"]);

// Panel de filtros de la hoja.
// fields: [{ id, label, type: category|number|date, groupLabel }]
// Los valores y recuentos llegan por funciones para no calcular campos cerrados.
export default function FiltersPanel({
    fields, groupOrder, resultLabel, activeCount, today,
    getSelected, getValues, getCounts, getRange, getUnit,
    onToggleValue, onSetValues, onSetRange, onClearField, onClearAll,
    views, activeViewId, onApplyView, onDeleteView, onSaveView, saveStatus, maxNameLength,
    initialFieldId, onClose,
}) {
    const [query, setQuery] = useState("");
    const hasValue = (field) => (field.type === "category"
        ? getSelected(field.id).length > 0
        : Boolean((getRange(field.type, field.id)?.min ?? "") !== "" || (getRange(field.type, field.id)?.max ?? "") !== ""));

    // Tarjetas: filtros con valor + los añadidos aún vacíos, en orden de alta.
    const [order, setOrder] = useState(() => {
        const active = fields.filter(hasValue).map((field) => field.id);
        return initialFieldId && !active.includes(initialFieldId) ? [...active, initialFieldId] : active;
    });
    const [openId, setOpenId] = useState(initialFieldId || null);
    const [openGroups, setOpenGroups] = useState(() => new Set(QUICK_GROUPS));
    const cardRefs = useRef(new Map());

    const fieldById = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);
    const activeIds = fields.filter(hasValue).map((field) => field.id);
    const cardIds = [...order.filter((id) => fieldById.has(id)), ...activeIds.filter((id) => !order.includes(id))];

    // Al abrir una tarjeta, llevarla a la vista.
    useEffect(() => {
        if (!openId) return;
        const node = cardRefs.current.get(openId);
        node?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    }, [openId]);

    // Abrir una tarjeta la fija en la lista: si se vacía mientras se edita, sigue
    // visible como "Sin valor" en vez de desaparecer bajo el cursor.
    const openCard = (fieldId) => {
        setOrder((current) => (current.includes(fieldId) ? current : [...current, fieldId]));
        setOpenId(fieldId);
    };
    const addField = (fieldId) => {
        openCard(fieldId);
        setQuery("");
    };
    const removeCard = (field) => {
        onClearField(field);
        setOrder((current) => current.filter((id) => id !== field.id));
        if (openId === field.id) setOpenId(null);
    };
    const clearAll = () => {
        onClearAll();
        setOrder([]);
        setOpenId(null);
    };

    const searching = Boolean(query.trim());
    const groups = useMemo(() => groupFilterFields(fields, { query, order: groupOrder }), [fields, query, groupOrder]);
    const firstResult = groups[0]?.fields[0];

    return (
        <SidePanel
            ariaLabel="Filtros de la hoja"
            title="Filtros"
            icon={<FiFilter />}
            subtitle={resultLabel}
            onClose={onClose}
            searchValue={query}
            onSearchChange={setQuery}
            searchPlaceholder="Buscar campo para filtrar…"
            onSearchEnter={() => { if (firstResult) addField(firstResult.id); }}
            footer={(
                <>
                    <button type="button" className="obras-panel-btn obras-panel-btn--ghost obras-panel-btn--danger" onClick={clearAll} disabled={!activeCount}>
                        <FiRefreshCw aria-hidden="true" /> Limpiar todo
                    </button>
                    <span className="obras-panel-foot__meta">{activeCount} {activeCount === 1 ? "filtro activo" : "filtros activos"}</span>
                    <button type="button" className="obras-panel-btn obras-panel-btn--primary obras-panel-foot__done" onClick={onClose}>Ver resultados</button>
                </>
            )}
        >
            {searching ? (
                <>
                    <p className="obras-panel-results">{groups.reduce((acc, group) => acc + group.fields.length, 0)} campos · Intro añade el primero</p>
                    {!groups.length ? <p className="obras-panel-empty">Ningún campo coincide con «{query.trim()}».</p> : null}
                    {groups.map((group) => (
                        <FieldGroup key={group.label} group={group} query={query} open onAdd={addField} activeIds={activeIds} cardIds={cardIds} />
                    ))}
                </>
            ) : (
                <>
                    <ViewsBar
                        label="Vistas guardadas"
                        views={views}
                        activeId={activeViewId}
                        onApply={(view) => { onApplyView(view); setOrder([]); setOpenId(null); }}
                        onDelete={onDeleteView}
                        onSave={onSaveView}
                        saveStatus={saveStatus}
                        maxNameLength={maxNameLength}
                        emptyHint="Ninguna todavía"
                    />

                    <section className="obras-panel-section">
                        <h3 className="obras-panel-eyebrow">Aplicados{cardIds.length ? ` (${activeIds.length})` : ""}</h3>
                        {!cardIds.length ? (
                            <p className="obras-panel-empty obras-panel-empty--soft">Sin filtros. Busca un campo arriba o elige uno de la lista.</p>
                        ) : (
                            <div className="obras-panel-cards">
                                {cardIds.map((id) => {
                                    const field = fieldById.get(id);
                                    const selected = field.type === "category" ? getSelected(id) : [];
                                    const range = field.type === "category" ? null : getRange(field.type, id);
                                    const summary = summarizeFilter(field.type, { selected, range });
                                    const open = openId === id;
                                    const TypeIcon = TYPE_ICONS[field.type] || FiList;
                                    return (
                                        <article
                                            key={id}
                                            ref={(node) => { if (node) cardRefs.current.set(id, node); else cardRefs.current.delete(id); }}
                                            className={`obras-panel-card${open ? " is-open" : ""}${summary ? "" : " is-empty"}`}
                                        >
                                            <div className="obras-panel-card__head">
                                                <button type="button" className="obras-panel-card__toggle" aria-expanded={open} onClick={() => (open ? setOpenId(null) : openCard(id))}>
                                                    <TypeIcon className="obras-panel-card__type" aria-hidden="true" />
                                                    <span className="obras-panel-card__label">{field.label}</span>
                                                    <span className="obras-panel-card__summary" title={summary || undefined}>{summary || "Sin valor"}</span>
                                                    <FiChevronDown className="obras-panel-card__chevron" aria-hidden="true" />
                                                </button>
                                                <button type="button" className="obras-panel-card__remove" aria-label={`Quitar filtro ${field.label}`} onClick={() => removeCard(field)}><FiX /></button>
                                            </div>
                                            {open ? (
                                                <div className="obras-panel-card__body">
                                                    {field.type === "category" ? (
                                                        <CategoryEditor
                                                            field={field}
                                                            values={getValues(id)}
                                                            selected={selected}
                                                            counts={getCounts(id)}
                                                            onToggle={(value) => onToggleValue(id, value)}
                                                            onSetValues={(values) => onSetValues(id, values)}
                                                        />
                                                    ) : (
                                                        <RangeEditor field={field} range={range} unit={getUnit?.(id)} today={today} onSetRange={onSetRange} />
                                                    )}
                                                </div>
                                            ) : null}
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <section className="obras-panel-section">
                        <h3 className="obras-panel-eyebrow">Añadir filtro</h3>
                        {groups.map((group) => (
                            <FieldGroup
                                key={group.label}
                                group={group}
                                query=""
                                open={openGroups.has(group.label)}
                                onToggleOpen={() => setOpenGroups((current) => {
                                    const next = new Set(current);
                                    if (next.has(group.label)) next.delete(group.label); else next.add(group.label);
                                    return next;
                                })}
                                onAdd={addField}
                                activeIds={activeIds}
                                cardIds={cardIds}
                            />
                        ))}
                    </section>
                </>
            )}
        </SidePanel>
    );
}

// Grupo de campos para añadir: los que ya tienen tarjeta salen marcados.
function FieldGroup({ group, query, open, onToggleOpen, onAdd, activeIds, cardIds }) {
    return (
        <div className={`obras-panel-fieldgroup${open ? " is-open" : ""}`}>
            {onToggleOpen ? (
                <button type="button" className="obras-panel-fieldgroup__head" aria-expanded={open} onClick={onToggleOpen}>
                    <FiChevronRight className="obras-panel-fieldgroup__chevron" aria-hidden="true" />
                    <span>{group.label}</span>
                    <span className="obras-panel-group__count">{group.fields.length}</span>
                </button>
            ) : (
                <p className="obras-panel-fieldgroup__title">{group.label}</p>
            )}
            {open ? (
                <div className="obras-panel-fields">
                    {group.fields.map((field) => {
                        const TypeIcon = TYPE_ICONS[field.type] || FiList;
                        const active = activeIds.includes(field.id);
                        const added = cardIds.includes(field.id);
                        return (
                            <button key={field.id} type="button" className={`obras-panel-field${active ? " is-active" : ""}`} onClick={() => onAdd(field.id)} title={added ? "Ya está en los filtros: abrir" : "Añadir filtro"}>
                                <TypeIcon aria-hidden="true" />
                                <span><Highlight text={field.label} query={query} /></span>
                                {active ? <i>activo</i> : null}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

// Lista de valores con buscador, recuento de obras y selección rápida.
function CategoryEditor({ field, values, selected, counts, onToggle, onSetValues }) {
    const [query, setQuery] = useState("");
    const searchRef = useRef(null);
    const options = useMemo(() => buildValueOptions(values, { selected, counts, query }), [values, selected, counts, query]);
    const needsSearch = values.length >= VALUE_SEARCH_THRESHOLD;
    const matchingUnselected = options.filter((option) => !option.selected).map((option) => option.value);

    // Autofoco en el buscador de valores solo con ratón/trackpad: en táctil abriría el teclado.
    useEffect(() => {
        const finePointer = typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches;
        if (needsSearch && finePointer) searchRef.current?.focus({ preventScroll: true });
    }, [needsSearch]);

    return (
        <div className="obras-panel-values">
            {needsSearch ? (
                <SearchField small inputRef={searchRef} value={query} onChange={setQuery} placeholder={`Buscar en ${field.label.toLowerCase()}…`} />
            ) : null}
            <div className="obras-panel-values__actions">
                <span>{selected.length ? `${selected.length} de ${values.length} elegidos` : `${values.length} valores`}</span>
                {matchingUnselected.length ? (
                    <button type="button" className="obras-panel-link" onClick={() => onSetValues([...selected, ...matchingUnselected])}>
                        {query.trim() ? "Elegir coincidencias" : "Elegir todos"}
                    </button>
                ) : null}
                {selected.length ? <button type="button" className="obras-panel-link" onClick={() => onSetValues([])}>Ninguno</button> : null}
            </div>
            {!options.length ? (
                <p className="obras-panel-empty">{values.length ? "Ningún valor coincide." : "Sin valores en las obras."}</p>
            ) : (
                <ul className="obras-panel-options obras-panel-options--values">
                    {options.map((option) => (
                        <li key={option.value}>
                            <label className={`obras-panel-option${option.selected ? " is-checked" : ""}${option.count ? "" : " is-zero"}`}>
                                <input type="checkbox" checked={option.selected} onChange={() => onToggle(option.value)} />
                                <span className="obras-panel-option__box" aria-hidden="true" />
                                <span className="obras-panel-option__label"><Highlight text={option.label} query={query} /></span>
                                <span className="obras-panel-option__count" title="Obras con este valor y el resto de filtros aplicados">{option.count}</span>
                            </label>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// Rango mínimo/máximo; las fechas tienen atajos relativos a hoy.
function RangeEditor({ field, range, unit, today, onSetRange }) {
    const isDate = field.type === "date";
    const min = range?.min ?? "";
    const max = range?.max ?? "";
    const preset = isDate ? activeDatePreset(range, today) : null;
    const applyPreset = (presetId) => {
        if (preset === presetId) { onSetRange(field.type, field.id, "min", ""); onSetRange(field.type, field.id, "max", ""); return; }
        const next = datePresetRange(presetId, today);
        onSetRange(field.type, field.id, "min", next.min);
        onSetRange(field.type, field.id, "max", next.max);
    };
    return (
        <div className="obras-panel-range">
            {isDate ? (
                <div className="obras-panel-presets" role="group" aria-label="Atajos de fecha">
                    {DATE_PRESETS.map((item) => (
                        <button key={item.id} type="button" aria-pressed={preset === item.id} className={`obras-panel-chip obras-panel-chip--small${preset === item.id ? " is-active" : ""}`} onClick={() => applyPreset(item.id)}>
                            {item.label}
                        </button>
                    ))}
                </div>
            ) : null}
            <div className="obras-panel-range__inputs">
                <label>
                    <span>{isDate ? "Desde" : "Mínimo"}</span>
                    <input autoFocus={!isDate} type={isDate ? "date" : "number"} value={min} onChange={(event) => onSetRange(field.type, field.id, "min", event.target.value)} />
                </label>
                <label>
                    <span>{isDate ? "Hasta" : "Máximo"}</span>
                    <input type={isDate ? "date" : "number"} value={max} onChange={(event) => onSetRange(field.type, field.id, "max", event.target.value)} />
                </label>
            </div>
            <p className="obras-panel-hint">{unit ? `En ${unit}. ` : ""}Deja un extremo vacío para no limitarlo.</p>
        </div>
    );
}
